import { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { tokensFor, type Pointer, type TokenSet } from "./tokens";

type ThemeName = "light" | "dark";

/**
 * Resolve the active theme. Precedence: explicit override > host
 * prefers-color-scheme > "light". Subscribes to runtime scheme changes.
 * Returns "light" when matchMedia is unavailable (old browsers / SSR / jsdom).
 */
export function useResolvedTheme(override?: ThemeName): ThemeName {
  const read = (): ThemeName => {
    if (override) return override;
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return "light";
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  };
  const [theme, setTheme] = useState<ThemeName>(read);

  useEffect(() => {
    if (override) { setTheme(override); return; }
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") { setTheme("light"); return; }
    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    setTheme(mql.matches ? "dark" : "light");
    const onChange = (e: { matches: boolean }) => setTheme(e.matches ? "dark" : "light");
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [override]);

  return theme;
}

/**
 * Resolve the primary pointer: "fine" only when the browser reports a fine
 * pointer (mouse, trackpad). Anything else, including a missing or stubbed
 * matchMedia (old browsers, SSR, jsdom), is "coarse" so controls keep the
 * touch-safe 44px geometry. Subscribes to runtime changes (a tablet docked to
 * a mouse).
 */
export function useResolvedPointer(): Pointer {
  const query = "(pointer: fine)";
  const read = (): Pointer => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return "coarse";
    return window.matchMedia(query).matches ? "fine" : "coarse";
  };
  const [pointer, setPointer] = useState<Pointer>(read);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") { setPointer("coarse"); return; }
    const mql = window.matchMedia(query);
    setPointer(mql.matches ? "fine" : "coarse");
    const onChange = (e: { matches: boolean }) => setPointer(e.matches ? "fine" : "coarse");
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  return pointer;
}

/**
 * Whether the visitor prefers reduced motion. Assumes NO preference (motion OK)
 * when matchMedia is unavailable (old browsers / SSR / jsdom), the same
 * fail-open precedent as the theme fallback. Resolved ONCE in the provider and
 * shared through MotionContext, so hundreds of buttons do not each subscribe.
 */
export function useResolvedReducedMotion(): boolean {
  const query = "(prefers-reduced-motion: reduce)";
  const read = (): boolean => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
    return window.matchMedia(query).matches;
  };
  const [reduced, setReduced] = useState<boolean>(read);
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") { setReduced(false); return; }
    const mql = window.matchMedia(query);
    setReduced(mql.matches);
    const onChange = (e: { matches: boolean }) => setReduced(e.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);
  return reduced;
}

const TokensContext = createContext<TokenSet | null>(null);
const MotionContext = createContext<boolean>(false);

/** True when the visitor asked for reduced motion: primitives then drop their CSS transitions (WCAG 2.3.3 / Task 12 check e). */
export function useReducedMotion(): boolean {
  return useContext(MotionContext);
}

/** A CSS `transition` value, or "none" under prefers-reduced-motion. */
export function motionSafe(reduced: boolean, transition: string): string {
  return reduced ? "none" : transition;
}

export function EmbedThemeProvider({ themeOverride, children }: { themeOverride?: ThemeName; children: ReactNode }) {
  const theme = useResolvedTheme(themeOverride);
  const pointer = useResolvedPointer();
  // Memoised: the provider value must be referentially stable between renders
  // that resolve to the same theme and pointer, or every consumer re-renders.
  const tokens = useMemo(() => tokensFor(theme, pointer), [theme, pointer]);
  const reducedMotion = useResolvedReducedMotion();
  return (
    <TokensContext.Provider value={tokens}>
      <MotionContext.Provider value={reducedMotion}>{children}</MotionContext.Provider>
    </TokensContext.Provider>
  );
}

/** Active token set. Throws if used outside an EmbedThemeProvider. */
export function useEmbedTokens(): TokenSet {
  const t = useContext(TokensContext);
  if (!t) throw new Error("useEmbedTokens must be used within an EmbedThemeProvider");
  return t;
}
